import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OperationSelectionActionType } from '@lib/components/operations-dashboard/operations-table/operations-table.component';
import { OperationState } from '@lib/models/box-additionnal-data';
import {
  CreationPersonneMorale,
  Investisseur,
  InvestisseurType,
  Operation,
  OperationType,
} from '@lib/models/generated/graphql';
import { QueryManagerService } from '@lib/services/queryManagerService';
import { gql } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import { CreationPMTab, OperationTab } from 'src/app/components/my-operations/consultant-tab';
import { DraftOperationsService } from 'src/service/draft-operation/draft-operation.service';
import { OperationTabsService } from 'src/service/operation-tabs.service';

const INVESTISSEUR_BY_ID = gql`
  query investisseurById($id: Long!) {
    investisseurById(id: $id) {
      id
      code
      investisseurEntite {
        displayName
        type
      }
    }
  }
`;
@Component({
  selector: 'app-my-operations-tab',
  standalone: false,
  templateUrl: './my-operations-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './my-operations-tab.component.scss',
})
export class MyOperationsTabComponent {
  private tabService = inject(OperationTabsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private queryManager = inject(QueryManagerService);
  private draftOperationService = inject(DraftOperationsService);

  protected tab: CreationPMTab | OperationTab;
  refreshFromNetworkOnLoad = false;

  allowedActions: OperationSelectionActionType[] = [OperationSelectionActionType.Terminate];

  ngOnInit() {
    this.refreshFromNetworkOnLoad = history.state?.refreshOperations === true;
    if (this.refreshFromNetworkOnLoad) {
      const { refreshOperations, ...navigationState } = history.state;
      history.replaceState(navigationState, '', location.href);
    }

    this.route.url.subscribe(urlSegments => {
      // get dashboard key in url path
      const currentPath = urlSegments.map(segment => segment.path).join('/');
      const foundTab = this.tabService.getTabByKey(currentPath);
      if (foundTab) {
        this.tab = foundTab;
      }
    });
  }

  isCreationTab(tab: CreationPMTab | OperationTab): tab is CreationPMTab {
    return tab.template === 'creation';
  }

  isOperationTab(tab: CreationPMTab | OperationTab): tab is OperationTab {
    return tab.template === 'operation';
  }

  onOperationClick(operation: Operation) {
    this.draftOperationService.reset();
    if (operation.statutId <= 5) {
      // statutId lower than 5 match to draft souscription that can be displayed in souscription page
      if (operation.operationType == OperationType.ActeDeGestion) {
        this.router.navigate(['actes', operation.id]);
      } else {
        this.router.navigate(['souscription', operation.id]);
      }
    } else {
      this.router.navigate(['operation', operation.id]);
    }
  }

  canConsultantSelectOperationToTerminate = (operation: Operation) => {
    return operation.statut.id === OperationState.SentToPartenaire;
  };

  async onDeclareSouscriptionFromCreationPM(creationPM: CreationPersonneMorale) {
    this.draftOperationService.reset();
    if (creationPM.atlasInvestisseurId) {
      const result = await firstValueFrom(
        this.queryManager.query<{ investisseurById: Investisseur }>({
          query: INVESTISSEUR_BY_ID,
          variables: {
            id: creationPM.atlasInvestisseurId,
          },
        })
      );

      if (result?.data?.investisseurById) {
        this.draftOperationService.reset();
        this.draftOperationService.investisseur = result.data.investisseurById;
        this.draftOperationService.investisseurType = InvestisseurType.PersonneMorale;
        this.router.navigate(['souscription']);
      }
    }
  }
}
